import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Table } from '@betterment/samba-react';
import { useFeatureFlag } from '@betterment/shared-components';

import { BillingPlan, BillingRateAssetBased, BillingRateFixed, BillingRateRange } from '../../../../graphql/schema';

const TieredRateTable: React.FC<{ plan: BillingPlan; platformFee: number }> = ({ plan, platformFee }) => {
  const { enabled: isNettingEnabled, loading: isNettingEnabledLoading } = useFeatureFlag({
    splitName: 'retail.b4a_platform_fee_netting_q3_2025_enabled',
    context: 'BillingPlanIndexPage',
  });

  const shouldShowNettingChanges = isNettingEnabled && !isNettingEnabledLoading;

  const getTiers = (): Array<BillingRateRange> => {
    if ('tiers' in plan.billingRate) {
      return plan.billingRate.tiers;
    }
    return [];
  };
  const tiers: Array<BillingRateRange> = getTiers();

  const tierRange = (tier: BillingRateRange, index): string => {
    const tierStart = index === 0 ? toMoney(0) : tiers[index - 1].tierEnd;
    const tierEndText = tier.tierEnd ? `- ${formatMoney(tier.tierEnd, 'defaultOptional')}` : 'and up';

    // @ts-expect-error TS2345 violates strict null checks
    return `${formatMoney(tierStart, 'defaultOptional')} ${tierEndText}`;
  };

  const tierRate = (tier: BillingRateRange): string => {
    if (tier.firmFee.__typename === 'BillingRateFixed') {
      return `${formatMoney(tier.firmFee.feeCents, 'defaultOptional')}`;
    }
    return `${tier.firmFee.feeBps} bps`;
  };

  const tierTotalRateFixed = (fee: BillingRateFixed): string => {
    return `${formatMoney(fee.feeCents, 'defaultOptional')}`;
  };

  const tierTotalBaseRateFixed = (fee: BillingRateFixed): string => {
    return `${tierTotalRateFixed(fee)} + ${platformFee} bps`;
  };

  const tierTotalRateAssetBased = (fee: BillingRateAssetBased): string => {
    return `${fee.feeBps + platformFee} bps`;
  };

  const tierTotalRate = ({ firmFee }: BillingRateRange): string => {
    if (firmFee.__typename === 'BillingRateFixed') {
      return tierTotalBaseRateFixed(firmFee);
    }
    return tierTotalRateAssetBased(firmFee);
  };

  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell css={{ pl: '0', pb: 'space.100' }}>Client AUM range</Table.ColumnHeaderCell>
          {shouldShowNettingChanges ? (
            <Table.ColumnHeaderCell css={{ pb: 'space.100' }}>Advisory fee</Table.ColumnHeaderCell>
          ) : (
            <>
              <Table.ColumnHeaderCell
                css={{
                  borderLeftStyle: 'solid',
                  borderXWidth: 'thin',
                  borderXColor: 'border',
                  pb: 'space.100',
                }}
              >
                Firm Fee
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell css={{ pb: 'space.100' }}>Client Fee</Table.ColumnHeaderCell>
            </>
          )}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {tiers.map((tier, index) => (
          <Table.Row key={`rateTier-${plan.id + index.toString()}`}>
            <Table.Cell css={{ pl: '0' }}>{tierRange(tier, index)}</Table.Cell>
            {shouldShowNettingChanges ? (
              <Table.Cell>{tierRate(tier)}</Table.Cell>
            ) : (
              <>
                <Table.Cell
                  css={{
                    borderLeftStyle: 'solid',
                    borderXWidth: 'thin',
                    borderXColor: 'border',
                  }}
                >
                  {tierRate(tier)}
                </Table.Cell>
                <Table.Cell>{tierTotalRate(tier)}</Table.Cell>
              </>
            )}
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

export default TieredRateTable;
